import { CurrencyDefinitionType } from 'data/Instrument/types'
import { CurrencySum } from 'data/Payments/types'
import { StateType } from 'data/types'
import isNil from 'lodash/isNil'
import reverse from 'lodash/reverse'
import sortBy from 'lodash/sortBy'
import { createSelector } from 'reselect'

import auth from '../Auth/selectors'
import * as currencies from '../Currencies/selectors'
import instrument from '../Instrument/selectors'
import { BalanceType, FormattedBalanceType, PortfolioType } from './types'

const getRawBalance = (state: StateType) => state.balance

const getLocalTotal: (state: StateType) => number = createSelector(
  getRawBalance,
  (balance) => balance.localTotal
)

const getLocalAvailable: (state: StateType) => number = createSelector(
  getRawBalance,
  (balance) => balance.localAvailable
)

const getAll: (state: StateType) => Array<FormattedBalanceType> = createSelector(
  getRawBalance,
  ({ all }) =>
    sortBy(all, 'currency').map((balance: FormattedBalanceType) => ({
      available: balance.available,
      available_local: balance.available_local,
      balance: balance.balance,
      balance_local: balance.balance_local,
      collateral: balance.collateral,
      currency: balance.currency,
      rate: balance.rate
    }))
)

const getAllAvailable: (state: StateType) => CurrencySum = createSelector(
  getAll,
  (all: Array<FormattedBalanceType>) =>
    all.reduce(
      (balances, item) => ({
        ...balances,
        [item.currency]: balances[item.currency]
          ? balances[item.currency] + item.available
          : item.available
      }),
      {}
    )
)

const getBalancesIndexed: (state: StateType) => {
  [key: string]: FormattedBalanceType
} = createSelector(getAll, (all) =>
  all.reduce((acc, balance) => ({ ...acc, [balance.currency]: balance }), {})
)

const getCurrent: (state: StateType) => void | BalanceType = createSelector(
  getRawBalance,
  instrument.getBaseSymbol,
  ({ all }, currency) => all.find((balance) => balance.currency === currency)
)

const getCounter: (state: StateType) => void | BalanceType = createSelector(
  getRawBalance,
  instrument.getCounterSymbol,
  ({ all }, currency) => all.find((balance) => balance.currency === currency)
)

const hasMoney: (state: StateType) => boolean = createSelector(
  getRawBalance,
  ({ all }) => all.length > 0
)

// TODO: fix when balance from websocket updated
const filterByCurrency: (
  all: Array<FormattedBalanceType>,
  currency: CurrencyDefinitionType
) => FormattedBalanceType = (all, currency) =>
  all.find((balance) => balance.currency === currency.key) || {
    available: 0,
    available_local: 0,
    balance: 0,
    balance_local: 0,
    collateral: 0,
    currency: currency.key,
    rate: 1.0
  }

const getPortfolioData: (state: StateType) => Array<PortfolioType> = createSelector(
  getLocalTotal,
  getBalancesIndexed,
  instrument.getCurrenciesIndexed,
  currencies.getIndexedCurrencies,
  auth.getLocalCurrency,
  (totalBalance, balances, instrumentCurrencies, currencies, localCurrency) => {
    const fiat: Array<PortfolioType> = []
    const crypto: Array<PortfolioType> = []
    const nullCrypto: Array<PortfolioType> = []

    for (let i = 0; i < Object.keys(instrumentCurrencies).length; i += 1) {
      const key = Object.keys(instrumentCurrencies)[i]
      if (!Object.prototype.hasOwnProperty.call(instrumentCurrencies, key)) continue

      const balance = balances[key]
      const noBalance = isNil(balance)
      const item = {
        asset: `${currencies[key].name} (${currencies[key].displaySymbol})`,
        availableBalance: noBalance ? undefined : balance.available,
        fiatValue: noBalance ? undefined : balance.balance_local,
        openOrders: noBalance ? undefined : balance.balance - balance.available,
        percentage: noBalance ? undefined : balance.balance_local / totalBalance,
        symbol: key as string,
        totalBalance: noBalance ? undefined : balance.balance
      }

      if (instrumentCurrencies[key].fiat) {
        if (key === localCurrency) {
          fiat.push(item)
        } else if (!noBalance && balance.balance > 0) {
          fiat.push(item)
        }
      } else if (noBalance) {
        nullCrypto.push(item)
      } else {
        crypto.push(item)
      }
    }

    return [
      ...reverse(sortBy(fiat, ['fiatValue'])),
      ...reverse(sortBy(crypto, ['fiatValue'])),
      ...sortBy(nullCrypto, ['asset'])
    ]
  }
)

export default {
  filterByCurrency,
  getAll,
  getAllAvailable,
  getBalancesIndexed,
  getCounter,
  getCurrent,
  getLocalAvailable,
  getLocalTotal,
  getPortfolioData,
  getRawBalance,
  hasMoney
}
